import { AKComponent } from "@theme/component";
import { DialogComponent } from '@theme/dialog-component';
import { getFocusableElements } from '@theme/focus';
import { ThemeEvents, CartAddEvent, ProductAddedEvent } from '@theme/events';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Builds product URL for API calls
 * @param {string | number} productId
 * @param {string} productHandle
 */
const buildProductUrl = (productId, productHandle) => {
    if (productHandle) {
        return `/products/${productHandle}.js`;
    } else if (productId) {
        return `/products/${productId}.js`;
    }
    throw new Error('Unable to build product URL - missing product handle');
};

/**
 * Fetches product data from Shopify API
 * @param {string | number} productId
 * @param {string} productHandle
 */
const fetchProductData = async (productId, productHandle) => {
    try {
        const productUrl = buildProductUrl(productId, productHandle);
        const response = await fetch(productUrl);
        
        if (!response.ok) {
            throw new Error(`HTTP Error: ${response.status}`);
        }
        
        // Shopify .js endpoints return JavaScript, not JSON
        // The content is valid JavaScript that can be parsed
        return response.json();
    } catch (error) {
        console.error('Error fetching product data:', error);
        throw error;
    }
};

/**
 * Adds product to cart via API
 * @param {string | number} variantId
 * @param {number} [quantity=1]
 */
const addToCart = async (variantId, quantity = 1) => {
    try {
        const formData = new FormData();
        formData.append('id', String(variantId));
        formData.append('quantity', String(quantity));

        // Use the standard Shopify cart add endpoint
        const cartAddUrl = /** @type {any} */ (window).theme?.routes?.cart_add_url || '/cart/add.js';
        const response = await fetch(cartAddUrl, {
            method: 'POST',
            body: formData,
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Cart Error: ${response.status}`);
        }

        // Shopify .js endpoints return JavaScript, not JSON
        // The content is valid JavaScript that can be parsed
        return response.json();
    } catch (error) {
        console.error('Error adding to cart:', error);
        throw error;
    }
};

// ============================================================================
// BASE QUICK VIEW COMPONENT
// ============================================================================

/**
 * Base class for quick view and quick add components
 * Provides common functionality for opening quick view dialogs
 */
class QuickViewBaseComponent extends AKComponent {
    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }

    /**
     * Gets product data from component attributes
     */
    getProductInfo() {
        const productId = this.dataset.productId;
        const productHandle = this.dataset.productHandle;
        const hasMultipleVariants = this.dataset.hasMultipleVariants === 'true';
        
        if (!productId && !productHandle) {
            throw new Error('Missing product ID or handle');
        }
        
        return { productId, productHandle, hasMultipleVariants };
    }

    /**
     * Opens quick view dialog
     */
    openQuickView(mode = 'quick-view') {
        const { productId, productHandle } = this.getProductInfo();
        
        const quickViewEvent = new CustomEvent('quick-view-open', {
            detail: { productId, productHandle, button: this, mode },
            bubbles: true
        });
        
        this.dispatchEvent(quickViewEvent);
    }
}

// ============================================================================
// QUICK VIEW BUTTON
// ============================================================================

/**
 * QuickViewButton - Component button that opens the quick view dialog
 * Uses on:click system from AKComponent
 */
class QuickViewButton extends QuickViewBaseComponent {
    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();
    }

    /**
     * Click handler for the on:click system
     * @param {Event} event
     */
    click(event) {
        event.preventDefault();
        this.openQuickView('quick-view');
    }

    /**
     * Entrée / Espace : ouverture directe (évite double déclenchement si un click synthétique suivait).
     * @param {KeyboardEvent} event
     */
    keydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ' && event.code !== 'Space') return;
        if (event.repeat) return;
        event.preventDefault();
        this.openQuickView('quick-view');
    }
}

// ============================================================================
// QUICK ADD BUTTON
// ============================================================================

/**
 * QuickAddButton - Component button that adds product directly to cart or opens variant selection
 * Uses on:click system from AKComponent
 */
class QuickAddButton extends QuickViewBaseComponent {
    /**
     * Click handler for the on:click system
     * @param {Event} event
     */
    async click(event) {
        event.preventDefault();
        
        try {
            const { hasMultipleVariants } = this.getProductInfo();
            
            if (hasMultipleVariants) {
                // If product has multiple variants, open quick view for selection
                this.openQuickView('quick-add');
            } else {
                // If product has only one variant, add directly to cart
                await this.#addToCartDirectly();
            }
        } catch (error) {
            console.error('QuickAddButton: Error:', error);
        }
    }

    /**
     * @param {KeyboardEvent} event
     */
    keydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ' && event.code !== 'Space') return;
        if (event.repeat) return;
        event.preventDefault();
        void this.click(event);
    }

    /**
     * Adds product directly to cart
     */
    async #addToCartDirectly() {
        try {
            const { productId, productHandle } = this.getProductInfo();
            
            // Get product data to find the variant ID
            const productData = await fetchProductData(productId, productHandle);
            const variant = productData.variants?.[0];
            
            if (!variant) {
                throw new Error('No variant found');
            }

            // Add to cart
            const cartData = await addToCart(variant.id, 1);
            
            // Use standard events from the project
            this.dispatchEvent(
                new CartAddEvent(cartData, this.id || '', {
                    source: 'quick-add-button',
                    itemCount: 1,
                    productId: productId || undefined,
                })
            );
            
            // Dispatch product added event for cart drawer refresh
            const productAddedEvent = new ProductAddedEvent(cartData, this.id || '', {
                source: 'quick-add-button',
                productId: productId || undefined,
                quantity: 1
            });
            document.dispatchEvent(productAddedEvent);
            
        } catch (error) {
            console.error('QuickAddButton: Error adding to cart:', error);
        }
    }
}

// ============================================================================
// QUICK VIEW DIALOG
// ============================================================================

/**
 * @typedef {Object} QuickViewDialogRefs
 * @property {HTMLElement} dialogTitle - The dialog title element
 * @property {HTMLElement} productContent - The product content container
 * @property {HTMLElement} productImage - The product image container
 * @property {HTMLElement} productTitle - The product title element
 * @property {HTMLElement} productPrice - The product price container
 * @property {HTMLElement} variantOptions - The variant options container
 * @property {HTMLSelectElement} variantSelect - The variant select element
 * @property {HTMLInputElement} quantityInput - The quantity input element
 * @property {HTMLAnchorElement} viewProductLink - The view product link (anchor)
 * @property {HTMLFormElement} productForm - The product form element
 * @property {HTMLButtonElement} addToCartButton - The add to cart button
 * @property {HTMLElement} beneficeSection - Bloc métafield benefice (metaobject)
 * @property {HTMLElement} beneficeTitre - Titre du benefice
 * @property {HTMLElement} beneficeDescription - Description HTML du benefice
 */

/**
 * QuickViewDialog - Dialog component that displays product information
 * @extends {DialogComponent}
 */
class QuickViewDialog extends DialogComponent {
    /** @type {HTMLDialogElement | null} */
    dialog = null;

    /** Élément qui a ouvert la modale (renvoi du focus à la fermeture). */
    /** @type {HTMLElement | null} */
    _openerElement = null;
    
    /** @type {any} */
    selectedVariant = null;
    
    /** @type {any} */
    productData = null;
    
    /** @type {string | null} */
    currentProductId = null;
    
    /** @type {string | null} */
    currentProductHandle = null;
    
    /** @type {string} */
    currentMode = 'quick-view';
    
    /** @type {((event: Event) => void) | null} */
    _cartUpdateHandler = null;

    /** @type {string} Données data-qv-benefice-titre (metaobject benefice) */
    _beneficeTitre = '';

    /** @type {string} Données data-qv-benefice-description (HTML, metaobject benefice) */
    _beneficeDescription = '';

    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();
        
        // Ensure dialog is set (from parent connectedCallback)
        if (!this.dialog) {
            this.dialog = this.querySelector('dialog');
        }
        
        // Listen for quick view events
        document.addEventListener('quick-view-open', this.#handleQuickViewOpen.bind(this));
        document.addEventListener('quick-view-close', this.#handleQuickViewClose.bind(this));
        
        // Set up form submission using on:submit system
        this.#setupFormSubmission();
    }

    disconnectedCallback() {
        // Remove scroll-lock attribute if dialog is still open
        if (this.dialog && this.dialog.open) {
            this.dialog.removeAttribute('scroll-lock');
        }
        
        super.disconnectedCallback();
        document.removeEventListener('quick-view-open', this.#handleQuickViewOpen);
        document.removeEventListener('quick-view-close', this.#handleQuickViewClose);
        
        if (this._cartUpdateHandler) {
            document.removeEventListener(ThemeEvents.cartUpdate, this._cartUpdateHandler);
            this._cartUpdateHandler = null;
        }
    }

    // ============================================================================
    // PUBLIC METHODS FOR ON: SYSTEM
    // ============================================================================

    /**
     * Show dialog method with scroll-lock
     */
    showDialog() {
        if (!this.dialog || this.dialog.open) return;

        // Add scroll-lock attribute to prevent page scrolling
        this.dialog.setAttribute('scroll-lock', '');
        
        super.showDialog();

        queueMicrotask(() => this.#focusFirstInDialog());
    }

    /**
     * Close dialog method for on:click system
     */
    closeDialog = () => {
        // Call parent closeDialog logic
        if (!this.dialog || !this.dialog.open) return;

        // Remove event listeners if they exist (aligné sur DialogComponent.closeDialog)
        if (this.boundHandleClick) {
            document.removeEventListener('click', this.boundHandleClick, true);
        }
        if (this.boundHandleKeyDown) {
            document.removeEventListener('keydown', this.boundHandleKeyDown, true);
        }
        window.removeEventListener('resize', this.boundHandleResize);
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = null;
        }

        // Add closing class for animation
        this.dialog.classList.add('c-dialog-closing');

        // Wait for animation to complete before closing
        const animationSpeed = getComputedStyle(document.documentElement)
            .getPropertyValue('--ak-dialog-animation-speed') || '0.4s';
        const duration = parseFloat(animationSpeed) * 1000;

        setTimeout(() => {
            if (this.dialog) {
                this.dialog.classList.remove('c-dialog-closing');
                // Remove scroll-lock attribute just before closing
                // This ensures the toggle event listener in dialog-component.js can properly remove it from documentElement
                if (this.dialog.hasAttribute('scroll-lock')) {
                    this.dialog.removeAttribute('scroll-lock');
                }
                this.dialog.close();
                // Also explicitly remove scroll-lock from documentElement as a safety measure
                document.documentElement.removeAttribute('scroll-lock');
            }

            const opener = this._openerElement;
            this._openerElement = null;
            if (opener instanceof HTMLElement && document.contains(opener)) {
                opener.focus();
            }
        }, duration);
    };

    // ============================================================================
    // SETUP METHODS
    // ============================================================================

    #setupFormSubmission() {
        if (!this.dialog) return;
        
        // Form submission will be handled by on:submit system
        // No need for manual event listener
    }

    // ============================================================================
    // EVENT HANDLERS
    // ============================================================================

    /**
     * Premier focus utile : corps de la modale si du contenu focusable existe, sinon bouton fermer.
     */
    #focusFirstInDialog() {
        if (!this.dialog?.open) return;

        const body = this.dialog.querySelector('.c-qv__body');
        const bodyFocusables = body ? getFocusableElements(body) : [];
        const dialogFocusables = getFocusableElements(this.dialog);
        const target = bodyFocusables[0] || dialogFocusables[0];
        target?.focus();
    }

    /**
     * @param {CustomEvent} event
     */
    #handleQuickViewOpen(event) {
        const { productId, productHandle, mode, button } = event.detail;

        this._openerElement = button instanceof HTMLElement ? button : null;

        if (button instanceof HTMLElement) {
            this._beneficeTitre = button.dataset.qvBeneficeTitre || '';
            this._beneficeDescription = button.dataset.qvBeneficeDescription || '';
        } else {
            this._beneficeTitre = '';
            this._beneficeDescription = '';
        }

        this.currentProductId = productId;
        this.currentProductHandle = productHandle;
        this.currentMode = mode || 'quick-view';
        
        // Add scroll-lock attribute to prevent page scrolling
        if (this.dialog) {
            this.dialog.setAttribute('scroll-lock', '');
        }
        
        this.showDialog();
        this.#loadProductData(productId, productHandle);
    }

    #handleQuickViewClose() {
        this.closeDialog();
    }

    // ============================================================================
    // PRODUCT DATA MANAGEMENT
    // ============================================================================

    /**
     * @param {string | number} productId
     * @param {string} productHandle
     */
    async #loadProductData(productId, productHandle) {
        try {
            this.#showState('loading');
            
            const productData = await fetchProductData(productId, productHandle);
            
            this.productData = productData;
            this.selectedVariant = productData.variants?.[0] || null;
            
            this.#displayProductData(productData);
            
        } catch (error) {
            console.error('QuickViewDialog: Error loading product:', error);
            this.#showState('error', 'Erreur lors du chargement du produit');
        }
    }

    // ============================================================================
    // STATE MANAGEMENT
    // ============================================================================

    /**
     * @param {string} state
     * @param {string} [message]
     */
    #showState(state, message = '') {
        // Hide all states first
        const productContent = this.refs.productContent;
        if (productContent && !Array.isArray(productContent) && productContent instanceof HTMLElement) {
            productContent.style.display = state === 'product-content' ? 'block' : 'none';
        }
    }

    // ============================================================================
    // PRODUCT DISPLAY
    // ============================================================================

    /**
     * @param {any} product
     */
    #displayProductData(product) {
        this.#updateDialogTitle();
        this.#updateProductInfo(product);
        this.#updateProductImage(product);
        this.#updateProductPrice();
        this.#updateVariantOptions();
        this.#updateVariantSelect();
        this.#updateViewProductLink();
        this.#adaptForMode();
        this.#addProductEventListeners();
        this.#setupCartEventListener();
        this.#updateBeneficeDisplay();

        this.#showState('product-content');

        queueMicrotask(() => this.#focusFirstInDialog());
    }

    /**
     * Affiche custom.benefice (metaobject titre + description) passés par data-* sur le bouton d’ouverture.
     */
    #updateBeneficeDisplay() {
        const section = this.refs.beneficeSection;
        const titreEl = this.refs.beneficeTitre;
        const descEl = this.refs.beneficeDescription;
        if (!section || Array.isArray(section) || !(section instanceof HTMLElement)) return;
        if (!titreEl || Array.isArray(titreEl) || !(titreEl instanceof HTMLElement)) return;
        if (!descEl || Array.isArray(descEl) || !(descEl instanceof HTMLElement)) return;

        const titre = (this._beneficeTitre || '').trim();
        const descRaw = this._beneficeDescription || '';
        const hasDesc = descRaw.trim().length > 0;

        if (!titre && !hasDesc) {
            section.setAttribute('hidden', '');
            titreEl.textContent = '';
            descEl.innerHTML = '';
            return;
        }

        section.removeAttribute('hidden');
        titreEl.textContent = titre;
        titreEl.hidden = !titre;

        if (hasDesc) {
            descEl.innerHTML = descRaw;
        } else {
            descEl.innerHTML = '';
        }
    }

    #updateDialogTitle() {
        const dialogTitle = this.refs.dialogTitle;
        if (dialogTitle && !Array.isArray(dialogTitle) && dialogTitle instanceof HTMLElement) {
            dialogTitle.textContent = this.currentMode === 'quick-add' ? 
                'Sélectionner une variante' : 'Quick View';
        }
    }

    /**
     * @param {any} product
     */
    #updateProductInfo(product) {
        // Update product title
        const productTitle = this.refs.productTitle;
        if (productTitle && !Array.isArray(productTitle) && productTitle instanceof HTMLElement) {
            productTitle.textContent = product.title || '';
        }
    }

    /**
     * @param {any} product
     */
    #updateProductImage(product) {
        const productImage = this.refs.productImage;
        if (!productImage || Array.isArray(productImage) || !(productImage instanceof HTMLElement)) return;

        const variantImage = this.selectedVariant?.featured_image || product.featured_image;
        
        if (variantImage) {
            productImage.innerHTML = `<img src="${variantImage}" alt="${product.title}" loading="lazy" decoding="async" width="400" height="400">`;
        } else {
            productImage.innerHTML = '<div class="c-qv__placeholder">Aucune image</div>';
        }
    }

    #updateProductPrice() {
        const productPrice = this.refs.productPrice;
        if (!productPrice || Array.isArray(productPrice) || !(productPrice instanceof HTMLElement) || !this.selectedVariant) return;

        const price = (this.selectedVariant.price / 100).toFixed(2);
        const comparePrice = this.selectedVariant.compare_at_price ? 
            (this.selectedVariant.compare_at_price / 100).toFixed(2) : null;

        if (comparePrice && parseFloat(comparePrice) > parseFloat(price)) {
            productPrice.innerHTML = `
                <span class="price price--sale">${price} €</span>
                <span class="price price--compare">${comparePrice} €</span>
            `;
        } else {
            productPrice.innerHTML = `<span class="price">${price} €</span>`;
        }
    }

    #updateVariantOptions() {
        const variantOptions = this.refs.variantOptions;
        if (!variantOptions || Array.isArray(variantOptions) || !(variantOptions instanceof HTMLElement) || !this.productData?.options) return;

        let optionsHTML = '';
        
        this.productData.options.forEach((/** @type {any} */ option, /** @type {number} */ optionIndex) => {
            if (option.values.length > 1) {
                // Check if this option has swatches
                const hasSwatches = option.values.some(/** @param {any} val */ (val) => {
                    return val.swatch && (val.swatch.color || val.swatch.image);
                });
                
                optionsHTML += `
                    <fieldset class="c-pdp__variants-fieldset">
                        <legend class="c-pdp__variants-legend">${option.name}</legend>
                        <div class="c-pdp__variants-grid">
                `;
                
                option.values.forEach(/** @param {any} valueObj */ (valueObj) => {
                    const value = typeof valueObj === 'string' ? valueObj : valueObj.value || valueObj;
                    const isSelected = this.selectedVariant?.[`option${optionIndex + 1}`] === value;
                    const isAvailable = this.#isVariantAvailable(optionIndex, value);
                    
                    // Get swatch data if available
                    const swatch = typeof valueObj === 'object' && valueObj.swatch ? valueObj.swatch : null;
                    const swatchColor = swatch?.color ? `rgb(${swatch.color.r}, ${swatch.color.g}, ${swatch.color.b})` : null;
                    const swatchImage = swatch?.image ? swatch.image : null;
                    const variantId = typeof valueObj === 'object' && valueObj.variant_id ? valueObj.variant_id : null;
                    
                    if (hasSwatches && swatch) {
                        // Use swatch component
                        const swatchStyle = swatchImage 
                            ? `--swatch-image: url(${swatchImage});` 
                            : swatchColor 
                                ? `color: ${swatchColor};` 
                                : '';
                        
                        optionsHTML += `
                            <label 
                                class="c-swatch c-swatch--disc${isSelected ? ' c-swatch--selected' : ''}${!isAvailable ? ' c-swatch--unavailable' : ''}"
                                for="quick-view-option-${optionIndex}-${value.replace(/\s+/g, '-').toLowerCase()}"
                                ${swatchStyle ? `style="${swatchStyle}"` : ''}
                            >
                                <input
                                    type="radio"
                                    class="c-swatch__input"
                                    name="option-${optionIndex}"
                                    value="${value}"
                                    id="quick-view-option-${optionIndex}-${value.replace(/\s+/g, '-').toLowerCase()}"
                                    data-option-index="${optionIndex}"
                                    data-option-value="${value}"
                                    ${isSelected ? 'checked' : ''}
                                    ${!isAvailable ? 'disabled aria-disabled="true"' : ''}
                                >
                                <span class="c-swatch__color"></span>
                            </label>
                        `;
                    } else {
                        // Use radio button component
                        optionsHTML += `
                            <label 
                                class="c-radio c-radio--button${!isAvailable ? ' c-radio--button--unavailable' : ''}"
                                for="quick-view-option-${optionIndex}-${value.replace(/\s+/g, '-').toLowerCase()}"
                            >
                                <input
                                    type="radio"
                                    class="c-radio__input"
                                    name="option-${optionIndex}"
                                    value="${value}"
                                    id="quick-view-option-${optionIndex}-${value.replace(/\s+/g, '-').toLowerCase()}"
                                    data-option-index="${optionIndex}"
                                    data-option-value="${value}"
                                    ${isSelected ? 'checked' : ''}
                                    ${!isAvailable ? 'disabled aria-disabled="true"' : ''}
                                >
                                <span class="c-radio__label">${value}</span>
                            </label>
                        `;
                    }
                });
                
                optionsHTML += `
                        </div>
                    </fieldset>
                `;
            }
        });
        
        variantOptions.innerHTML = optionsHTML;
        
        // Re-add event listeners for newly created variant inputs
        this.#addVariantInputListeners();
    }

    #updateVariantSelect() {
        const variantSelect = this.refs.variantSelect;
        if (!variantSelect || Array.isArray(variantSelect) || !(variantSelect instanceof HTMLSelectElement) || !this.productData?.variants) return;

        let optionsHTML = '<option value="">Sélectionner une variante</option>';
        
        this.productData.variants.forEach(/** @param {any} variant */ (variant) => {
            const selected = variant.id === this.selectedVariant?.id ? 'selected' : '';
            optionsHTML += `<option value="${variant.id}" ${selected}>${variant.title}</option>`;
        });
        
        variantSelect.innerHTML = optionsHTML;
        variantSelect.style.display = 'block';
    }

    #updateViewProductLink() {
        const viewProductLink = this.refs.viewProductLink;
        if (viewProductLink && !Array.isArray(viewProductLink) && viewProductLink instanceof HTMLAnchorElement && this.currentProductHandle) {
            viewProductLink.href = `/products/${this.currentProductHandle}`;
        }
    }

    #adaptForMode() {
        const viewProductLink = this.refs.viewProductLink;
        const isLink = viewProductLink && !Array.isArray(viewProductLink) && viewProductLink instanceof HTMLElement;

        if (this.currentMode === 'quick-add') {
            // Change submit button text
            const addToCartButton = this.refs.addToCartButton;
            if (addToCartButton && !Array.isArray(addToCartButton) && addToCartButton instanceof HTMLButtonElement) {
                addToCartButton.textContent = 'Ajouter au panier';
            }

            // Hide view product link in quick-add mode
            if (isLink) {
                viewProductLink.style.display = 'none';
            }
        } else if (isLink) {
            viewProductLink.style.display = '';
        }
    }

    // ============================================================================
    // VARIANT MANAGEMENT
    // ============================================================================

    /**
     * @param {number} optionIndex
     * @param {string} optionValue
     */
    #isVariantAvailable(optionIndex, optionValue) {
        if (!this.productData?.variants) return false;
        
        return this.productData.variants.some(/** @param {any} variant */ (variant) => {
            const variantOptionValue = variant[`option${optionIndex + 1}`];
            return variantOptionValue === optionValue && variant.available;
        });
    }

    /**
     * @param {number} optionIndex
     * @param {string} optionValue
     */
    #selectVariantOption(optionIndex, optionValue) {
        if (!this.productData?.variants) return;

        const newSelectedVariant = this.productData.variants.find(/** @param {any} variant */ (variant) => {
            const variantOptionValue = variant[`option${optionIndex + 1}`];
            if (variantOptionValue !== optionValue) return false;
            
            for (let i = 0; i < this.productData.options.length; i++) {
                if (i === optionIndex) continue;
                
                const currentOptionValue = this.selectedVariant?.[`option${i + 1}`];
                if (currentOptionValue && variant[`option${i + 1}`] !== currentOptionValue) {
                    return false;
                }
            }
            
            return true;
        });

        if (newSelectedVariant) {
            this.selectedVariant = newSelectedVariant;
            
            const variantSelect = this.refs.variantSelect;
            if (variantSelect && !Array.isArray(variantSelect) && variantSelect instanceof HTMLSelectElement) {
                variantSelect.value = String(newSelectedVariant.id);
            }
            
            this.#updateProductDisplay();
            this.#updateVariantOptionStates();
        }
    }

    #updateProductDisplay() {
        if (!this.productData) return;

        this.#updateProductImage(this.productData);
        this.#updateProductPrice();
    }

    #updateVariantOptionStates() {
        const variantOptions = this.refs.variantOptions;
        if (!this.productData || !this.selectedVariant || !variantOptions || Array.isArray(variantOptions) || !(variantOptions instanceof HTMLElement)) return;

        const variantInputs = variantOptions.querySelectorAll('input[type="radio"][data-option-index]');
        variantInputs.forEach(/** @param {HTMLElement} input */ (input) => {
            if (!(input instanceof HTMLInputElement)) return;
            const optionIndex = parseInt(input.dataset.optionIndex || '0');
            const optionValue = input.value || '';
            
            const isSelected = this.selectedVariant[`option${optionIndex + 1}`] === optionValue;
            const isAvailable = this.#isVariantAvailable(optionIndex, optionValue);
            
            // Update checked state
            input.checked = isSelected;
            
            // Update disabled state
            input.disabled = !isAvailable;
            if (!isAvailable) {
                input.setAttribute('aria-disabled', 'true');
            } else {
                input.removeAttribute('aria-disabled');
            }
            
            // Update parent label classes
            const label = input.closest('label');
            if (label) {
                label.classList.toggle('c-swatch--selected', isSelected && label.classList.contains('c-swatch'));
                label.classList.toggle('c-radio--button--unavailable', !isAvailable && label.classList.contains('c-radio'));
                label.classList.toggle('c-swatch--unavailable', !isAvailable && label.classList.contains('c-swatch'));
            }
        });
    }

    /**
     * @param {Event} event
     */
    #handleVariantChange(event) {
        const target = /** @type {HTMLSelectElement} */ (event.target);
        const variantId = target.value;
        if (!variantId || !this.productData?.variants) return;

        const newSelectedVariant = this.productData.variants.find(/** @param {any} v */ (v) => v.id.toString() === variantId.toString());
        if (newSelectedVariant) {
            this.selectedVariant = newSelectedVariant;
            this.#updateProductDisplay();
            this.#updateVariantOptionStates();
        }
    }

    // ============================================================================
    // EVENT LISTENERS
    // ============================================================================

    #addProductEventListeners() {
        // Add event listeners for variant option inputs
        this.#addVariantInputListeners();

        // Add event listener for variant select
        const variantSelect = this.refs.variantSelect;
        if (variantSelect && !Array.isArray(variantSelect) && variantSelect instanceof HTMLSelectElement) {
            variantSelect.addEventListener('change', this.#handleVariantSelectChange.bind(this));
        }

        // Add event listener for quantity input
        const quantityInput = this.refs.quantityInput;
        if (quantityInput && !Array.isArray(quantityInput) && quantityInput instanceof HTMLInputElement) {
            quantityInput.addEventListener('input', this.#handleQuantityInput.bind(this));
        }
    }

    #addVariantInputListeners() {
        const variantOptions = this.refs.variantOptions;
        if (!variantOptions || Array.isArray(variantOptions) || !(variantOptions instanceof HTMLElement)) return;
        
        const variantInputs = variantOptions.querySelectorAll('input[type="radio"][data-option-index]');
        variantInputs.forEach(/** @param {HTMLElement} input */ (input) => {
            if (input instanceof HTMLInputElement) {
                input.addEventListener('change', this.#handleVariantInputChange.bind(this));
            }
        });
    }

    /**
     * @param {Event} event
     */
    #handleVariantInputChange(event) {
        const input = /** @type {HTMLInputElement} */ (event.target);
        
        // Safety checks
        if (!input || !input.dataset || input.type !== 'radio') {
            return;
        }
        
        const optionIndex = input.dataset.optionIndex;
        const optionValue = input.value;
        
        if (optionIndex === undefined || !optionValue) {
            return;
        }
        
        const optionIndexNum = parseInt(optionIndex);
        if (isNaN(optionIndexNum)) {
            return;
        }
        
        if (input.disabled) return;
        
        this.#selectVariantOption(optionIndexNum, optionValue);
    }

    /**
     * @param {Event} event
     */
    #handleVariantSelectChange(event) {
        this.#handleVariantChange(event);
    }

    /**
     * @param {Event} event
     */
    #handleQuantityInput(event) {
        // Optional: Add any quantity-specific logic here
    }

    #setupCartEventListener() {
        if (this._cartUpdateHandler) {
            document.removeEventListener(ThemeEvents.cartUpdate, this._cartUpdateHandler);
        }

        this._cartUpdateHandler = (/** @type {CustomEvent} */ event) => {
            if (event.detail?.source === 'product-form-component') {
                setTimeout(() => {
                    this.closeDialog();
                }, 500);
            }
        };

        document.addEventListener(ThemeEvents.cartUpdate, this._cartUpdateHandler);
    }

    // ============================================================================
    // FORM SUBMISSION
    // ============================================================================

    // This method will be called by the on:submit system
    /**
     * @param {Event} event
     */
    async handleSubmit(event) {
        event.preventDefault();
        
        if (!this.selectedVariant) {
            alert('Veuillez sélectionner une variante');
            return;
        }

        try {
            const quantityInput = this.refs.quantityInput;
            const quantity = (quantityInput && !Array.isArray(quantityInput) && quantityInput instanceof HTMLInputElement && quantityInput.value) ? parseInt(quantityInput.value) : 1;
            
            // Add to cart
            const data = await addToCart(this.selectedVariant.id, quantity);
            
            // Use standard events from the project
            this.dispatchEvent(
                new CartAddEvent(data, this.id || '', {
                    source: 'quick-view-dialog',
                    itemCount: quantity,
                    productId: this.currentProductId || undefined,
                })
            );
            
            // Dispatch product added event for cart drawer refresh
            const productAddedEvent = new ProductAddedEvent(data, this.id || '', {
                source: 'quick-view-dialog',
                productId: this.currentProductId || undefined,
                quantity: quantity
            });
            document.dispatchEvent(productAddedEvent);
            
            // Close dialog after successful add (faster in quick-add mode)
            const closeDelay = this.currentMode === 'quick-add' ? 200 : 500;
            setTimeout(() => {
                this.closeDialog();
            }, closeDelay);
            
        } catch (error) {
            console.error('Error adding to cart:', error);
            alert('Erreur lors de l\'ajout au panier');
        }
    }

    // ============================================================================
    // UTILITY METHODS
    // ============================================================================

    retryLoad() {
        if (this.currentProductId && this.currentProductHandle) {
            this.#loadProductData(String(this.currentProductId), this.currentProductHandle || '');
        }
    }
}

// ============================================================================
// COMPONENT REGISTRATION
// ============================================================================

// Register custom components
if (!customElements.get('quick-view-button')) {
    customElements.define('quick-view-button', QuickViewButton);
}

if (!customElements.get('quick-add-button')) {
    customElements.define('quick-add-button', QuickAddButton);
}

if (!customElements.get('quick-view-dialog')) {
    customElements.define('quick-view-dialog', QuickViewDialog);
}